import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { toast } from 'sonner';
import { CreditCard, CheckCircle2, Sparkles, Crown, Loader, ArrowUpRight, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import InvoicesTable from '@/components/billing/InvoicesTable';
import PayoutBanner from '@/components/billing/PayoutBanner';
import PlanCTA from '@/components/billing/PlanCTA';

const TIER_ICONS = {
  starter: Sparkles,
  pro: CreditCard,
  enterprise: Crown,
};
const TIER_ACCENT = {
  starter: 'border-slate-200 bg-white',
  pro: 'border-emerald-500 bg-emerald-50/30',
  enterprise: 'border-slate-900 bg-slate-50',
};

const Billing = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [payoutInfo, setPayoutInfo] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [downloadingId, setDownloadingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgradingId, setUpgradingId] = useState(null);
  const [pollingSession, setPollingSession] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    try {
      const [p, s, u, po, me, inv] = await Promise.all([
        axios.get(`${API}/billing/plans`),
        axios.get(`${API}/billing/subscription`),
        axios.get(`${API}/billing/usage`),
        axios.get(`${API}/billing/payout-info`),
        axios.get(`${API}/admin/platform/me`),
        axios.get(`${API}/billing/invoices`),
      ]);
      setPlans(p.data?.plans || []);
      setSubscription(s.data);
      setUsage(u.data);
      setPayoutInfo(po.data);
      setIsSuperAdmin(!!me.data?.is_super_admin);
      setInvoices(inv.data?.invoices || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load billing info');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDownloadInvoice = async (sessionId, invoiceNumber) => {
    setDownloadingId(sessionId);
    try {
      const res = await axios.get(`${API}/billing/invoices/${sessionId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not download invoice');
    } finally {
      setDownloadingId(null);
    }
  };

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Handle return-from-Stripe — poll status
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const cancelled = searchParams.get('cancelled');
    if (cancelled) {
      toast.message('Checkout cancelled — no charge was made.');
      const next = new URLSearchParams(searchParams);
      next.delete('cancelled');
      setSearchParams(next, { replace: true });
      return;
    }
    if (!sessionId || pollingSession) return;
    setPollingSession(sessionId);
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const r = await axios.get(`${API}/billing/checkout/status/${sessionId}`);
        if (r.data.payment_status === 'paid') {
          toast.success('Subscription activated!');
          setPollingSession(null);
          const next = new URLSearchParams(searchParams);
          next.delete('session_id');
          setSearchParams(next, { replace: true });
          refresh();
          return;
        }
        if (r.data.status === 'expired') {
          toast.error('Checkout session expired.');
          setPollingSession(null);
          return;
        }
        if (attempts < 8) {
          setTimeout(tick, 2500);
        } else {
          toast.message('Payment is still processing. Refresh in a moment.');
          setPollingSession(null);
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Could not verify payment');
        setPollingSession(null);
      }
    };
    tick();
  }, [searchParams, pollingSession, refresh, setSearchParams]);

  const handleUpgrade = async (planId) => {
    if (!isAdmin) {
      toast.error('Only org admins can upgrade');
      return;
    }
    setUpgradingId(planId);
    try {
      const r = await axios.post(`${API}/billing/checkout`, {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      if (r.data.url) {
        window.location.href = r.data.url;
      } else {
        toast.error('No checkout URL returned');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start checkout');
      setUpgradingId(null);
    }
  };

  const handleRenew = async () => {
    if (!isAdmin) {
      toast.error('Only org admins can renew');
      return;
    }
    setUpgradingId(currentTier);
    try {
      const r = await axios.post(`${API}/billing/renew`, { origin_url: window.location.origin });
      if (r.data.url) {
        window.location.href = r.data.url;
      } else {
        toast.error('No renewal URL returned');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start renewal');
      setUpgradingId(null);
    }
  };

  // Determine if a renewal nudge banner should show (≤7 days to expiry or already past)
  const renewalBanner = (() => {
    if (!subscription || subscription.plan_tier === 'starter' || !subscription.expires_at) return null;
    const exp = new Date(subscription.expires_at).getTime();
    const now = Date.now();
    const daysLeft = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
    if (daysLeft > 7) return null;
    if (daysLeft > 0) {
      return { kind: 'warn', text: `Your ${subscription.plan?.name} plan renews in ${daysLeft} day${daysLeft === 1 ? '' : 's'}.` };
    }
    if (daysLeft >= -7) {
      return { kind: 'danger', text: `Your subscription lapsed ${-daysLeft} day${daysLeft === -1 ? '' : 's'} ago. You're in the grace period — renew today to avoid a downgrade.` };
    }
    return null;
  })();

  const currentTier = subscription?.plan_tier || 'starter';

  const renderUsageRow = (label, used, cap, key) => {
    const unlimited = cap < 0 || cap == null;
    const pct = unlimited ? 0 : Math.min(100, Math.round((used / Math.max(cap, 1)) * 100));
    const getBarColor = () => {
      if (pct > 90) return 'bg-red-500';
      if (pct > 75) return 'bg-amber-500';
      return 'bg-emerald-500';
    };
    const barColor = getBarColor();
    return (
      <div className="space-y-1" data-testid={`usage-row-${key}`}>
        <div className="flex justify-between text-xs font-ibm">
          <span className="text-slate-600">{label}</span>
          <span className="tabular-nums text-slate-900">
            {used.toLocaleString()} {unlimited ? '/ unlimited' : `/ ${cap.toLocaleString()}`}
          </span>
        </div>
        <div className="h-2 bg-slate-100 rounded-sm overflow-hidden">
          {!unlimited && (
            <div className={`h-full ${barColor}`} style={{ width: `${pct}%` }} />
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-8 md:p-12" data-testid="billing-loading">
        <div className="text-slate-600 font-ibm">Loading…</div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-12" data-testid="billing-page">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <CreditCard size={32} className="text-slate-900" />
            <div>
              <h1 className="text-4xl font-chivo font-bold text-slate-900 tracking-tight" data-testid="billing-title">
                Billing
              </h1>
              <p className="text-slate-600 mt-1 font-ibm">
                Manage your subscription and view current usage. Active plan:{' '}
                <strong className="text-slate-900 capitalize" data-testid="billing-active-tier">
                  {currentTier}
                </strong>
                {subscription?.expires_at && currentTier !== 'starter' && (
                  <>
                    {' '}— renews{' '}
                    <span className="text-slate-900">
                      {new Date(subscription.expires_at).toLocaleDateString()}
                    </span>
                  </>
                )}
              </p>
            </div>
          </div>
          {pollingSession && (
            <div className="flex items-center gap-2 text-emerald-700" data-testid="billing-polling">
              <Loader size={16} className="animate-spin" />
              <span className="text-sm font-ibm">Verifying payment…</span>
            </div>
          )}
        </div>

        {/* Payout destination banner — extracted to PayoutBanner. */}
        <PayoutBanner
          payoutInfo={payoutInfo}
          isSuperAdmin={isSuperAdmin}
          onManage={() => navigate('/admin/platform-bank')}
        />

        {renewalBanner && (
          <div
            className={`mb-6 rounded-sm border p-4 flex items-center justify-between gap-4 flex-wrap ${
              renewalBanner.kind === 'danger'
                ? 'border-red-200 bg-red-50 text-red-900'
                : 'border-amber-200 bg-amber-50 text-amber-900'
            }`}
            data-testid="billing-renewal-banner"
          >
            <div className="flex items-center gap-3">
              <CreditCard size={18} />
              <span className="font-ibm font-semibold text-sm">{renewalBanner.text}</span>
            </div>
            <Button
              onClick={handleRenew}
              disabled={!isAdmin || !!upgradingId}
              data-testid="billing-renew-now-button"
              className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm hover:scale-[0.98] transition-transform"
            >
              {upgradingId ? (
                <><Loader size={14} className="mr-2 animate-spin" /> Redirecting…</>
              ) : (
                <>Renew now <ArrowUpRight size={14} className="ml-2" /></>
              )}
            </Button>
          </div>
        )}

        {/* Usage panel */}
        {usage && (
          <div className="bg-white border border-slate-200 rounded-sm p-6 mb-8" data-testid="billing-usage-card">
            <h2 className="text-lg font-chivo font-bold text-slate-900 mb-4">This Month&apos;s Usage</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {renderUsageRow(
                'Transactions',
                usage.usage.transactions_per_month,
                usage.caps.transactions_per_month,
                'transactions'
              )}
              {renderUsageRow('Webhooks', usage.usage.webhooks, usage.caps.webhooks, 'webhooks')}
              {renderUsageRow('API Keys', usage.usage.api_keys, usage.caps.api_keys, 'api-keys')}
            </div>
          </div>
        )}

        {/* Plans */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="billing-plans-grid">
          {plans.map((plan) => {
            const Icon = TIER_ICONS[plan.id] || CreditCard;
            const isCurrent = currentTier === plan.id;
            const canUpgrade = plan.price_usd > 0 && !isCurrent;
            return (
              <motion.div
                key={plan.id}
                whileHover={{ y: -2 }}
                className={`rounded-sm border-2 p-6 flex flex-col ${TIER_ACCENT[plan.id] || 'border-slate-200 bg-white'}`}
                data-testid={`plan-card-${plan.id}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Icon size={20} className="text-slate-900" />
                    <span className="font-chivo font-bold text-lg text-slate-900">{plan.name}</span>
                  </div>
                  {isCurrent && (
                    <span className="text-[10px] font-ibm font-semibold uppercase tracking-widest bg-slate-900 text-white px-2 py-0.5 rounded-sm" data-testid={`plan-current-${plan.id}`}>
                      Current
                    </span>
                  )}
                </div>
                <div className="mb-4">
                  <span className="text-4xl font-chivo font-bold text-slate-900 tabular-nums">
                    ${plan.price_usd.toFixed(0)}
                  </span>
                  <span className="text-slate-500 font-ibm text-sm"> / month</span>
                </div>
                <ul className="space-y-2 mb-6 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm font-ibm text-slate-700">
                      <CheckCircle2 size={14} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <PlanCTA
                  plan={plan}
                  isCurrent={isCurrent}
                  canUpgrade={canUpgrade}
                  isAdmin={isAdmin}
                  upgradingId={upgradingId}
                  onUpgrade={handleUpgrade}
                />
                {!isAdmin && plan.price_usd > 0 && (
                  <p className="text-[10px] font-ibm text-slate-400 mt-2 text-center">
                    Org admin required to upgrade
                  </p>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Invoices section — extracted to InvoicesTable. */}
        <InvoicesTable
          invoices={invoices}
          downloadingId={downloadingId}
          onDownload={handleDownloadInvoice}
        />

        <div className="mt-8 p-4 bg-slate-50 border border-slate-200 rounded-sm flex items-start gap-3" data-testid="billing-info">
          <XCircle size={16} className="text-slate-500 flex-shrink-0 mt-0.5" />
          <div className="text-xs font-ibm text-slate-600">
            <strong className="text-slate-900">Test mode:</strong> This integration uses Stripe test keys.
            Use test card <code className="px-1 bg-white border border-slate-200 rounded-sm">4242 4242 4242 4242</code>{' '}
            with any future expiry and any CVC to complete a sandbox checkout.
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Billing;
